import { useEffect } from 'react';
import { TYPES_DE_FICHE, type FicheDeBulle } from '@nextilt/commun';
import type { ChangementDeFiche, FicheEnBase } from '../partage/api/types';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import {
  commeLeProjet,
  FERMER_LA_FICHE,
  FICHE_PAS_ECRITE,
  MOTS_DES_TYPES_DE_FICHE,
  REGLAGES,
  TYPE_DE_FICHE,
  typeDeLaFiche,
} from '../partage/vocabulaire';
import './HautDeLaFiche.css';

/** Le type et le papier d'une fiche, tels qu'elle les montre, et le geste qui les change. */
export interface TypeEtPapierDeLaFiche {
  /** Ce que la fiche montre : `ficheDeLaBulle` (`@nextilt/commun`), jamais recalculé ici. */
  montree: FicheDeBulle;
  /** Ce que la bulle a choisi elle-même (`null` : comme le projet). */
  enBase: FicheEnBase;
  /** Ce que son projet donne, pour dire « Comme le projet · Classique ». */
  duProjet: FicheDeBulle;
  /** Le dernier choix n'a pas pu s'enregistrer : la fiche est revenue à ce qu'elle était. */
  pasEcrit: boolean;
  onChoisir: (changement: ChangementDeFiche) => void;
}

/**
 * La ligne du haut de la fiche de l'ordinateur (chantier types-de-fiche, modification 02 ;
 * maquette du carnet `fiche-quatre-types.html`, `.f-haut`) : à gauche « Type : Classique ▾ », à
 * droite « ⚙ Réglages » et la croix. Le téléphone garde sa tête (`NavDeLaFiche`). « ⚙ Réglages »
 * retourne la fiche en panneau, et la remet à l'endroit (`PanneauDesReglages`, chantier
 * reglages-de-la-fiche, modification 02).
 */
export function HautDeLaFiche({
  fiche,
  panneauOuvert,
  onReglages,
  onFermer,
}: {
  fiche: TypeEtPapierDeLaFiche | null;
  /** Le panneau Réglages a pris la place de la fiche. */
  panneauOuvert: boolean;
  onReglages: () => void;
  onFermer: () => void;
}) {
  return (
    <div className="fiche-haut">
      {fiche ? <MenuDuType fiche={fiche} /> : <span />}
      <div className="fiche-haut-droite">
        <button
          type="button"
          className="fiche-reglages"
          aria-pressed={panneauOuvert}
          onClick={onReglages}
        >
          <span className="fiche-reglages-pastille">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
            </svg>
            {REGLAGES}
          </span>
        </button>
        <button
          type="button"
          className="fiche-fermer"
          data-tuto="fiche-fermer"
          onClick={onFermer}
          aria-label={FERMER_LA_FICHE}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * « Type : Classique ▾ » et son petit menu : les quatre types, et « Comme le projet » quand la
 * bulle a son propre choix. Le papier est passé dans l'intercalaire Visuel des Réglages
 * (reglages-de-la-fiche, modification 02). Un choix s'écrit aussitôt ; le menu reste ouvert.
 * Échap ou un clic à côté le referment sans fermer la fiche.
 */
function MenuDuType({ fiche }: { fiche: TypeEtPapierDeLaFiche }) {
  const menu = useMenuOuvert('garde');
  const { montree, enBase, duProjet, pasEcrit, onChoisir } = fiche;

  /*
   * Un clic à côté du menu, sur l'écran transparent de la fiche (qui la referme au clic) : il
   * referme le menu seulement. Le clic qui suit ce `pointerdown` ne va pas jusqu'à l'écran.
   */
  const { ouvert } = menu;
  useEffect(() => {
    if (!ouvert) return;
    const surLEcran = (e: PointerEvent) => {
      if (!(e.target instanceof Element) || !e.target.closest('.fiche-ecran')) return;
      const avaler = (c: MouseEvent) => {
        if (c.target instanceof Element && c.target.closest('.fiche-ecran')) c.stopPropagation();
      };
      document.addEventListener('click', avaler, { capture: true, once: true });
    };
    document.addEventListener('pointerdown', surLEcran, true);
    return () => document.removeEventListener('pointerdown', surLEcran, true);
  }, [ouvert]);

  return (
    <div className="fiche-type" ref={menu.ref}>
      <button
        type="button"
        className="fiche-type-bouton"
        aria-haspopup="true"
        aria-expanded={menu.ouvert}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        <span className="fiche-type-pastille">{typeDeLaFiche(MOTS_DES_TYPES_DE_FICHE[montree.type])} ▾</span>
      </button>

      {menu.ouvert && (
        <div className="fiche-type-menu">
          <div className="fiche-type-groupe" role="group" aria-label={TYPE_DE_FICHE}>
            <p className="fiche-type-intertitre">{TYPE_DE_FICHE}</p>
            {enBase.typeDeFiche !== null && (
              <button type="button" className="fiche-type-choix fiche-type-choix--projet" onClick={() => onChoisir({ typeDeFiche: null })}>
                {commeLeProjet(MOTS_DES_TYPES_DE_FICHE[duProjet.type])}
              </button>
            )}
            {TYPES_DE_FICHE.map((t) => (
              <button
                key={t}
                type="button"
                className={`fiche-type-choix${t === montree.type ? ' est-choisi' : ''}`}
                aria-pressed={t === montree.type}
                onClick={() => onChoisir({ typeDeFiche: t })}
              >
                {MOTS_DES_TYPES_DE_FICHE[t]}
              </button>
            ))}
          </div>
          {pasEcrit && (
            <p className="fiche-type-erreur" role="alert">
              {FICHE_PAS_ECRITE}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
